import { OfferNetworkProvider } from "@redotech/crdb-enums/marketing/offer-network-provider";
import {
  DiscoPageType,
  discoPageTypeBySurface,
  DiscoWidgetFormat,
  OfferPlacementSurface,
  UptickChannel,
  UptickPlacementType,
  uptickRoutingByPlacementSurface,
} from "@redotech/redo-integrations-definitions-common/offer-network/offer-placement";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

const resolvedRoktOfferConfigSchema_ = z.object({
  provider: z.literal(OfferNetworkProvider.Rokt),
  accountId: z.string(),
  pageIdentifier: z.string(),
  sandbox: z.boolean(),
});
const resolvedFalconOfferConfigSchema_ = z.object({
  provider: z.literal(OfferNetworkProvider.Falcon),
  /** The publisher public key (falcon_pub_...) — client-safe by design. */
  apiKey: z.string(),
  placementId: z.string(),
  isLiveMode: z.boolean(),
});

const resolvedUptickOfferConfigSchema_ = z.object({
  provider: z.literal(OfferNetworkProvider.Uptick),
  /** Redo-global, and a path segment of a browser-visible URL — not a secret. */
  placeId: z.string(),
  placement: createEnum(Object.values(UptickPlacementType)),
  /** Sent on every offer request as `app_id`; what Uptick reports us on. */
  appExternalId: z.string(),
  /**
   * The merchant's storefront domain, sent only on the web channel. A tracking
   * page is served from a Redo or custom domain, so without this Uptick files
   * it under a different site than the Shopify extensions and the merchant's
   * reporting splits in two. It is the canonical domain rather than
   * *.myshopify.com, because that is what the Shopify SDK reports as the page
   * location and therefore what the two channels have to agree on.
   */
  siteId: z.string().optional(),
});

const resolvedDiscoOfferConfigSchema_ = z.object({
  provider: z.literal(OfferNetworkProvider.Disco),
  /** The publisher live public key — client-safe by design. */
  apiKey: z.string(),
  widgetId: z.string(),
  widgetFormat: createEnum(Object.values(DiscoWidgetFormat)),
  pageType: createEnum(Object.values(DiscoPageType)),
  sandbox: z.boolean(),
});

/** Client-safe, render-ready SDK config resolved server-side. */
const resolvedOfferConfigSchema_ = z.discriminatedUnion("provider", [
  resolvedRoktOfferConfigSchema_,
  resolvedFalconOfferConfigSchema_,
  resolvedUptickOfferConfigSchema_,
  resolvedDiscoOfferConfigSchema_,
]);
export type ResolvedUptickOfferConfig = z.infer<
  typeof resolvedUptickOfferConfigSchema_
>;
export type ResolvedOfferConfig =
  | z.infer<typeof resolvedRoktOfferConfigSchema_>
  | z.infer<typeof resolvedFalconOfferConfigSchema_>
  | ResolvedUptickOfferConfig
  | z.infer<typeof resolvedDiscoOfferConfigSchema_>;
export const resolvedOfferConfigSchema: z.ZodType<ResolvedOfferConfig> =
  resolvedOfferConfigSchema_;

/**
 * Uptick's client config for a stored placement, or null on a surface Uptick
 * has given us no placement type for. The place and placement type come from
 * the surface, never from the document, so every caller agrees on them.
 */
export function uptickOfferConfig(
  placement: { surface: OfferPlacementSurface; appExternalId: string },
  /** The merchant's storefront domain; only the web channel reports a site. */
  options?: { siteDomain?: string },
): ResolvedUptickOfferConfig | null {
  const routing = uptickRoutingByPlacementSurface[placement.surface];
  if (!routing) {
    return null;
  }
  return {
    provider: OfferNetworkProvider.Uptick,
    placeId: routing.placeId,
    placement: routing.placement,
    appExternalId: placement.appExternalId,
    ...(routing.channel === UptickChannel.WEB && options?.siteDomain
      ? { siteId: options.siteDomain }
      : {}),
  };
}

/**
 * Disco's client config for a stored placement, or null on a surface Disco has
 * no page type for. The page type comes from the surface, never the document.
 */
export function discoOfferConfig(
  placement: {
    surface: OfferPlacementSurface;
    widgetId: string;
    widgetFormat: DiscoWidgetFormat;
  },
  apiKey: string,
): z.infer<typeof resolvedDiscoOfferConfigSchema_> | null {
  const pageType = discoPageTypeBySurface[placement.surface];
  if (!pageType) {
    return null;
  }
  return {
    provider: OfferNetworkProvider.Disco,
    apiKey,
    widgetId: placement.widgetId,
    widgetFormat: placement.widgetFormat,
    pageType,
    sandbox: false,
  };
}

const offerPlacementSummarySchema_ = z.object({
  id: z.string(),
  provider: createEnum(Object.values(OfferNetworkProvider)),
  name: z.string(),
  surface: createEnum(Object.values(OfferPlacementSurface)),
  config: resolvedOfferConfigSchema_,
});
export type OfferPlacementSummary = z.infer<
  typeof offerPlacementSummarySchema_
>;
export const offerPlacementSummarySchema: z.ZodType<OfferPlacementSummary> =
  offerPlacementSummarySchema_;
